import React, { useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Star, ArrowUp, Loader2 } from 'lucide-react';
import { useCommentsByBookInfinite } from '../hooks/useCommentsByBook';
import { usePostComment } from '../hooks/usePostComment';
import type { Comment } from '../api/comments';

interface BookCommentsBottomSheetProps {
  bookId: number;
  isOpen: boolean;
  onClose: () => void;
}

const BookCommentsBottomSheet: React.FC<BookCommentsBottomSheetProps> = ({ bookId, isOpen, onClose }) => {
  const [inputValue, setInputValue] = useState('');
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [replyTo, setReplyTo] = useState<Comment | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    comments,
    totalCount,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError: error,
    refetch,
  } = useCommentsByBookInfinite(bookId, {
    enabled: isOpen && !!bookId,
    limit: 10,
  });

  const { mutate: postComment, isPending } = usePostComment({
    onSuccess: () => {
      setInputValue('');
      setRating(0);
      setReplyTo(null);
      // Refresh comments list after posting
      refetch();
    },
  });

  // Handle scroll to load more
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasNextPage || isFetchingNextPage) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        fetchNextPage();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
      setRating(0);
      setHoverRating(0);
      setReplyTo(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const totalComments = totalCount;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed || !bookId) return;

    const isReply = !!replyTo;
    if (!isReply && rating === 0) return;

    postComment({
      book_id: bookId,
      parent_review_id: isReply ? replyTo!.review_id : null,
      comment: trimmed,
      rating: isReply ? undefined : rating || undefined,
    });
  };

  const formatPersianDate = (dateString?: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    try {
      const parts = new Intl.DateTimeFormat('fa-IR-u-ca-persian-nu-latn', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      }).formatToParts(date);
      const day = parts.find((p) => p.type === 'day')?.value || '';
      const month = parts.find((p) => p.type === 'month')?.value || '';
      if (day && month) return `${Number(day)} ${month}`;
      return parts.map((p) => p.value).join(' ');
    } catch {
      return '';
    }
  };

  const Avatar: React.FC<{ src?: string; alt: string }> = ({ src, alt }) => (
    <img
      src={src || 'https://api.dicebear.com/7.x/avataaars/svg?seed=guest&backgroundColor=f3f4f6&clothesColor=9ca3af'}
      alt={alt}
      className="w-8 h-8 rounded-full object-cover border border-gray-200 flex-shrink-0"
      loading="lazy"
    />
  );

  const CommentItem: React.FC<{ comment: Comment }> = ({ comment }) => {
    const [showReplies, setShowReplies] = useState(false);
    const repliesCount = Array.isArray(comment.replies) ? comment.replies.length : 0;
    const hasReplies = repliesCount > 0;

    return (
      <div className="flex gap-3">
        <Avatar src={comment.avatar_url} alt={comment.name || `کاربر ${comment.user_id}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 min-w-0">
              <p className="text-xs font-semibold text-foreground font-persian truncate">
                {comment.name || `کاربر ${comment.user_id}`}
              </p>
              {typeof comment.rating === 'number' && comment.rating > 0 && (
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={'w-3 h-3 transition-colors ' + (comment.rating >= star ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300')}
                    />
                  ))}
                </div>
              )}
            </div>
            {comment.created_at && (
              <span className="text-[10px] text-muted-foreground font-persian whitespace-nowrap">
                {formatPersianDate(comment.created_at)}
              </span>
            )}
          </div>
          <p className="text-sm text-black font-medium font-persian leading-relaxed whitespace-pre-line mt-1">
            {comment.comment}
          </p>
          <div className="mt-1 flex items-center gap-3 text-[10px] text-muted-foreground font-persian">
            <button
              type="button"
              onClick={() => {
                setReplyTo(comment);
                inputRef.current?.focus();
              }}
              className="hover:text-foreground transition-colors"
            >
              پاسخ
            </button>
            {hasReplies && (
              <button
                type="button"
                onClick={() => setShowReplies((prev) => !prev)}
                className="text-[10px] text-primary-600 hover:text-primary-700 font-persian"
              >
                {showReplies ? 'پنهان کردن پاسخ‌ها' : `مشاهده پاسخ‌ها (${repliesCount.toLocaleString('fa-IR')})`}
              </button>
            )}
          </div>
          {hasReplies && showReplies && (
            <div className="mt-2 space-y-2 border-r border-gray-100 pr-3">
              {comment.replies!.map((rep) => (
                <div key={rep.review_id} className="flex gap-2">
                  <Avatar src={rep.avatar_url} alt={rep.name || `کاربر ${rep.user_id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <p className="text-[11px] font-semibold text-foreground font-persian truncate">
                        {rep.name || `کاربر ${rep.user_id}`}
                      </p>
                      {rep.created_at && (
                        <span className="text-[9px] text-muted-foreground font-persian whitespace-nowrap">
                          {formatPersianDate(rep.created_at)}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-foreground font-persian leading-relaxed whitespace-pre-line">
                      {rep.comment}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const disabledSend = !inputValue.trim() || isPending || (!replyTo && rating === 0);

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/40 z-40"
            onClick={onClose}
          />

          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 35 }}
            drag="y"
            dragDirectionLock
            dragConstraints={{ top: 0, bottom: 400 }}
            dragElastic={{ top: 0, bottom: 0.25 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 500) onClose();
            }}
             className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-2 pb-0"
          >
            <div
              className="w-full max-w-lg bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[80vh]"
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
              dir="rtl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pt-2 pb-1 px-4 border-b border-gray-100">
                <div className="w-10 h-1.5 bg-gray-300 rounded-full mx-auto mb-2" />
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={onClose}
                    className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                    aria-label="بستن نظرات"
                  >
                    <X className="w-5 h-5 text-muted-foreground" />
                  </button>
                  <p className="text-xs font-persian text-muted-foreground">
                    {totalComments.toLocaleString('fa-IR')} نظر
                  </p>
                </div>
              </div>

              <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
                {isLoading && (
                  <div className="flex items-center justify-center py-10 text-xs text-muted-foreground font-persian">
                    در حال بارگذاری نظرات...
                  </div>
                )}
                {error && (
                  <div className="flex items-center justify-center py-10 text-xs text-destructive font-persian">
                    خطا در بارگذاری نظرات
                  </div>
                )}
                {!isLoading && !error && comments && comments.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <div className="text-4xl mb-2">💬</div>
                    <p className="text-xs text-muted-foreground font-persian">
                      اولین نفری باشید که درباره این کتاب نظر میدهید
                    </p>
                  </div>
                )}
                {!isLoading && !error && comments && comments.length > 0 && (
                  <div className="space-y-4">
                    {comments.map((c) => (
                      <CommentItem key={c.review_id} comment={c} />
                    ))}
                    {isFetchingNextPage && (
                      <div className="flex justify-center py-2">
                        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
                      </div>
                    )}
                  </div>
                )}
              </div>

                <form onSubmit={handleSubmit} className="border-t border-gray-100 px-4 pt-2 pb-0 bg-white">
                <AnimatePresence initial={false}>
                  {replyTo && (
                    <motion.div
                      key="reply-indicator"
                      initial={{ opacity: 0, y: 6, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 4, scale: 0.98 }}
                      transition={{ duration: 0.2 }}
                      className="flex items-center justify-between mb-1 bg-gray-50 rounded-lg px-3 py-1"
                    >
                      <p className="text-[11px] text-muted-foreground font-persian truncate">
                        در حال پاسخ به {replyTo.name || `کاربر ${replyTo.user_id}`}
                      </p>
                      <button type="button" onClick={() => setReplyTo(null)} className="text-[11px] text-primary-600 font-persian">
                        لغو
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex items-center justify-between mb-3 px-2">
                  <span className="text-xs font-semibold text-gray-700 font-persian">به این کتاب چه امتیازی میدهی؟</span>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className="p-0.5 transition-transform hover:scale-110"
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setRating(star)}
                      >
                        <Star className={'w-5 h-5 transition-colors ' + ((hoverRating || rating) >= star ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300')} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-row-reverse gap-2 pb-0">
                  <input
                    type="text"
                    dir="rtl"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={replyTo ? 'پاسخ...' : 'نظرتو بنویس'}
                    autoComplete="off"
                    ref={inputRef}
                     className="flex-1 h-12 text-xs md:text-sm font-persian rounded-2xl bg-gray-100 px-3 border border-transparent focus:outline-none focus:ring-2 focus:ring-primary focus:bg-white placeholder:text-gray-400 text-right"
                     style={{ lineHeight: '48px' }}
                  />
                  <button
                    type="submit"
                    disabled={disabledSend}
                     className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-full bg-primary-500 text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed shadow-sm transition-transform active:scale-95"
                    aria-label="ارسال نظر"
                  >
                    <ArrowUp className="w-5 h-5 rotate-90" />
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BookCommentsBottomSheet;
